/**
 * cardGrouping.js
 *
 * Gemeinsame Utility-Funktionen für Karten-Gruppierung und Sortierung.
 * Wird von useDeckViewState und allen Views genutzt.
 */
import { typeOptions } from '../../../components/uiDefinitions';

// Default Kategorien sind Kartentypen - Drops auf diese sollten verhindert werden
export const DEFAULT_TYPE_CATEGORIES = new Set(typeOptions.map(t => t.toLowerCase()));
// "other" ist auch eine default Kategorie
DEFAULT_TYPE_CATEGORIES.add('other');

export function isDefaultTypeCategory(category) {
  if (!category || typeof category !== 'string') return false;
  return DEFAULT_TYPE_CATEGORIES.has(category.toLowerCase());
}

export function getPrimaryTypeCategory(card) {
  if (!card?.types || !Array.isArray(card.types) || card.types.length === 0) {
    return 'other';
  }
  const firstType = String(card.types[0] || '').toLowerCase().trim();
  return firstType || 'other';
}

export function canCardBeDroppedIntoDefaultCategory(card, defaultCategory) {
  if (!isDefaultTypeCategory(defaultCategory)) return false;
  const normalizedCategory = String(defaultCategory).toLowerCase().trim();
  const primaryType = getPrimaryTypeCategory(card);
  return primaryType === normalizedCategory;
}

/**
 * Sortiere Karten nach dem angegebenen Kriterium.
 *
 * @param {Object[]} cards - Array von Kartenobjekten
 * @param {string} sortBy - Sortierkriterium ('name', 'cmc', 'power', 'toughness')
 * @returns {Object[]} Sortiertes Array (neue Kopie)
 */
export function sortCards(cards, sortBy) {
  return [...cards].sort((a, b) => {
    switch (sortBy) {
      case 'cmc':
        return (a.cmc || 0) - (b.cmc || 0);
      case 'power':
        return (parseInt(a.power) || 0) - (parseInt(b.power) || 0);
      case 'toughness':
        return (parseInt(a.toughness) || 0) - (parseInt(b.toughness) || 0);
      case 'name':
      default:
        return (a.name || '').localeCompare(b.name || '');
    }
  });
}

/**
 * Gruppiere Karten nach einer Strategy mit Sortierung.
 *
 * @param {Object[]} cards - Array von Kartenobjekten
 * @param {Object} strategy - Grouping Strategy aus groupingStrategies.js
 * @param {string} sortBy - Sortierkriterium innerhalb der Gruppen
 * @returns {Map<string, Object[]>} Map von Gruppe → sortierte Karten
 */
export function groupCardsByStrategy(cards, strategy, sortBy) {
  const groups = new Map();

  for (const card of cards) {
    const group = strategy.groupFn(card);
    if (!groups.has(group)) {
      groups.set(group, []);
    }
    groups.get(group).push({ ...card, displayCategory: group });
  }

  // Sortiere Karten innerhalb jeder Gruppe
  for (const [group, groupCards] of groups) {
    groups.set(group, sortCards(groupCards, sortBy));
  }

  return groups;
}
